import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, Skeleton, EmptyState, AButton, SearchInput } from "@/components/ios";
import { Home, ChevronRight, BedDouble, Bath, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"];
const TYPES = ["Apartment", "House", "Townhouse", "Unit", "Studio"];
const EMPTY = { address: "", suburb: "", state: "NSW", postcode: "", beds: 2, baths: 1, type: "Apartment" };

export default function Properties() {
  const [props, setProps] = useState(null);
  const [q, setQ] = useState("");
  const [stateF, setStateF] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/properties").then(({ data }) => setProps(data));
  useEffect(() => { load(); }, []);

  const states = useMemo(() => Array.from(new Set((props || []).map((p) => p.state))).sort(), [props]);
  const filtered = useMemo(() => {
    if (!props) return [];
    const query = q.trim().toLowerCase();
    return props.filter((p) => {
      if (stateF !== "all" && p.state !== stateF) return false;
      if (!query) return true;
      return [p.address, p.suburb, p.postcode].filter(Boolean).some((s) => String(s).toLowerCase().includes(query));
    });
  }, [props, q, stateF]);

  const submit = async () => {
    if (!form.address.trim() || !form.suburb.trim() || !form.postcode.trim()) {
      toast.error("Address, suburb and postcode are required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/properties", { ...form, beds: Number(form.beds), baths: Number(form.baths) });
      setOpen(false); setForm(EMPTY);
      toast.success("Property added");
      nav(`/properties/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add property"); }
    finally { setBusy(false); }
  };

  const field = (k) => ({ value: form[k], onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });
  const inputCls = "w-full text-sm rounded-lg px-3 py-2 outline-none border focus:border-[var(--accent)]";
  const inputStyle = { background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" };

  return (
    <div>
      <LargeTitle subtitle="Managed properties and current tenancies"
        right={<AButton testid="add-property-btn" onClick={() => setOpen(true)}><Plus size={15} className="inline mr-1 -mt-0.5" /> Add property</AButton>}>
        Properties
      </LargeTitle>

      {props && props.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SearchInput testid="properties-search" value={q} onChange={setQ} placeholder="Search by address, suburb, postcode…" />
          <select data-testid="properties-state-filter" value={stateF} onChange={(e) => setStateF(e.target.value)}
            className="text-sm rounded-full px-4 py-2 outline-none border"
            style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}>
            <option value="all">All states</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {!props ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-64" />)}</div>
      ) : props.length === 0 ? (
        <Surface><EmptyState icon={Home} title="No properties yet" subtitle="Add your first property to start inspecting."
          action={<AButton testid="empty-add-property-btn" onClick={() => setOpen(true)}>Add property</AButton>} /></Surface>
      ) : filtered.length === 0 ? (
        <Surface><EmptyState icon={Home} title="No matching properties" subtitle="Try a different search or state filter." /></Surface>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => {
            const t = p.tenancy?.tenants?.[0];
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.03 }}>
                <Surface className="overflow-hidden cursor-pointer active:scale-[0.99] transition-transform"
                  data-testid={`property-card-${i}`} onClick={() => nav(`/properties/${p.id}`)}>
                  <div className="h-36 bg-cover bg-center" style={{ backgroundImage: `url(${p.image})` }} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate" style={{ color: "var(--text)" }}>{p.address}</div>
                        <div className="text-sm text-secondary">{p.suburb}, {p.state} {p.postcode}</div>
                      </div>
                      <Pill tone="accent">{p.state}</Pill>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm text-secondary">
                      <span className="flex items-center gap-1"><BedDouble size={14} /> {p.beds}</span>
                      <span className="flex items-center gap-1"><Bath size={14} /> {p.baths}</span>
                      <span>{p.type}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                      <span className="text-sm text-secondary truncate">{t ? `Tenant · ${t.name}` : "Vacant"}</span>
                      <ChevronRight size={16} className="text-secondary" />
                    </div>
                  </div>
                </Surface>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <DialogHeader><DialogTitle style={{ color: "var(--text)" }}>Add a property</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="prop-address" placeholder="Street address" className={inputCls} style={inputStyle} {...field("address")} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="prop-suburb" placeholder="Suburb" className={inputCls} style={inputStyle} {...field("suburb")} />
              <input data-testid="prop-postcode" placeholder="Postcode" className={inputCls} style={inputStyle} {...field("postcode")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="prop-state" className={inputCls} style={inputStyle} {...field("state")}>
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="prop-type" className={inputCls} style={inputStyle} {...field("type")}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-secondary">Bedrooms
                <input data-testid="prop-beds" type="number" min="0" className={`${inputCls} mt-1`} style={inputStyle} {...field("beds")} />
              </label>
              <label className="text-xs text-secondary">Bathrooms
                <input data-testid="prop-baths" type="number" min="0" className={`${inputCls} mt-1`} style={inputStyle} {...field("baths")} />
              </label>
            </div>
          </div>
          <AButton testid="confirm-add-property" onClick={submit} disabled={busy} className="w-full mt-2">
            {busy ? "Adding…" : "Add property"}
          </AButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}
